import type {
  CompleteMcpOAuthCallback,
  McpOAuthMessages,
} from '@terra/shared-contracts-mcp-servers';

export async function authorizeMcpOAuthWithElectron({
  fetchAuthorizeUrl,
  completeOAuthCallback,
  messages,
}: {
  fetchAuthorizeUrl: () => Promise<string>;
  completeOAuthCallback: (input: {
    code: string;
    state: string;
  }) => Promise<void>;
  messages: McpOAuthMessages;
}): Promise<boolean> {
  const authorizeUrl = await fetchAuthorizeUrl();

  if (window.electronAPI?.shell?.openExternal && window.electronAPI.mcpOAuth) {
    const cancelAuthorizationWaitRef: { current: (() => void) | null } = {
      current: null,
    };
    const authorizationResult = new Promise<{
      code?: string;
      state?: string;
    }>((resolve, reject) => {
      let unsubscribeSuccess: (() => void) | null = null;
      let unsubscribeError: (() => void) | null = null;
      let timeout: number | null = null;

      const finish = () => {
        if (timeout !== null) {
          window.clearTimeout(timeout);
        }

        unsubscribeSuccess?.();
        unsubscribeError?.();
      };

      cancelAuthorizationWaitRef.current = finish;
      timeout = window.setTimeout(() => {
        finish();
        reject(new Error(messages.oauthAuthorizationTimedOut));
      }, 120_000);
      const handleSuccess = (payload: { code?: string; state?: string }) => {
        finish();
        resolve(payload);
      };

      const handleError = ({ error }: { error: string }) => {
        finish();
        reject(new Error(error || messages.oauthAuthorizationFailed));
      };

      unsubscribeSuccess = window.electronAPI.mcpOAuth.onSuccess(handleSuccess);
      unsubscribeError = window.electronAPI.mcpOAuth.onError(handleError);
    });

    const result = await window.electronAPI.shell.openExternal(authorizeUrl);

    if (!result.success) {
      cancelAuthorizationWaitRef.current?.();

      throw new Error(result.error ?? messages.oauthAuthorizationFailed);
    }

    const callbackPayload = await authorizationResult;

    if (callbackPayload.code && callbackPayload.state) {
      await completeOAuthCallback({
        code: callbackPayload.code,
        state: callbackPayload.state,
      });
    }

    return true;
  }

  throw new Error(messages.oauthPopupBlocked);
}

export const electronMcpOAuthStrategy = {
  openReservedWindow: () => null,
  authorize: async ({
    fetchAuthorizeUrl,
    completeOAuthCallback,
    messages,
    onError,
  }: {
    fetchAuthorizeUrl: () => Promise<string>;
    completeOAuthCallback?: CompleteMcpOAuthCallback;
    messages: McpOAuthMessages;
    onError: (input: { title: string; description?: string }) => void;
  }): Promise<boolean> => {
    if (!completeOAuthCallback) {
      onError({
        title: messages.oauthAuthorizationFailed,
        description: messages.oauthAuthorizationIncomplete,
      });

      return false;
    }

    try {
      await authorizeMcpOAuthWithElectron({
        fetchAuthorizeUrl,
        completeOAuthCallback,
        messages,
      });

      return true;
    } catch (error: unknown) {
      onError({
        title: messages.oauthAuthorizationFailed,
        description: error instanceof Error ? error.message : undefined,
      });

      return false;
    }
  },
};
